import { useCallback } from "react";
import { LandTechEndpoints, routedClient } from "react-migration/lib/persistence/apollo";
import {
  useDeleteWorkspaceFileMutation,
  useGetWorkspaceFileUploadUrlMutation,
  useRegisterWorkspaceFileUploadMutation,
} from "@gql/sites-api/graphql";

export interface WorkspaceUploadCallbacks {
  onUpload: (file: File) => Promise<string>; // Returns UploadedFile ID
  onDelete: (fileId: string) => Promise<boolean>;
}

export function useWorkspaceUploadCallbacks(): WorkspaceUploadCallbacks {
  const [getSignedUrl] = useGetWorkspaceFileUploadUrlMutation({
    context: { endpoint: LandTechEndpoints.Sites },
    client: routedClient,
  });

  const [registerUpload] = useRegisterWorkspaceFileUploadMutation({
    context: { endpoint: LandTechEndpoints.Sites },
    client: routedClient,
  });

  const [deleteFile] = useDeleteWorkspaceFileMutation({
    context: { endpoint: LandTechEndpoints.Sites },
    client: routedClient,
  });

  const onUpload = useCallback(
    async (file: File): Promise<string> => {
      const { data: urlData, errors: urlErrors } = await getSignedUrl({
        variables: { filename: file.name, mimetype: file.type },
      });
      if (urlErrors?.length || !urlData?.getWorkspaceFileUploadUrl) throw new Error("Failed to get signed URL");

      const { signedUrl, s3Key } = urlData.getWorkspaceFileUploadUrl;
      const response = await fetch(signedUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!response.ok) throw new Error("S3 upload failed");

      const { data: regData, errors: regErrors } = await registerUpload({ variables: { s3Key } });
      if (regErrors?.length || !regData?.registerWorkspaceFileUpload) throw new Error("Registration failed");

      return regData.registerWorkspaceFileUpload;
    },
    [getSignedUrl, registerUpload]
  );

  const onDelete = useCallback(
    async (fileId: string): Promise<boolean> => {
      const { data, errors } = await deleteFile({ variables: { fileId } });
      if (errors?.length || !data?.deleteWorkspaceFile) throw new Error("Delete failed");
      return data.deleteWorkspaceFile;
    },
    [deleteFile]
  );

  return { onUpload, onDelete };
}
